<template>
    <section class="container mx-auto">
        <form @submit.prevent="submitCurrentStep()" class="w-full mx-auto pb-24">
            <!-- Introduction text -->
            <div class="text-center py-10">
                <p v-text="$t('DASHBOARD_FRIEND')"></p>
            </div>

            <div class="max-w-sm mx-auto">
                <!-- Regions select field -->
                <regions-field v-model="region"/>

                <!-- Source select field -->
                <sources-field v-model="parentSource" label="Are you doing bikes, doors, event, etc?"/>

                <sources-field
                    v-if="showSubSourcesField"
                    v-model="source"
                    :parent-id="parentSource.id"
                    :key="parentSource.id"/>

                <!-- Small gap -->
                <div class="clear--sm"></div>

                <!-- Form button -->
                <div class="text-center">
                    <submit-button
                        @click="submitCurrentStep()" :processing="isFormProcessing"
                        v-text="$t('DASHBOARD_NEXT_STEP')"></submit-button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import required from 'vuelidate/src/validators/required';
import SourcesField from '@/components/forms/fields/SourceField';
import RegionsField from '@/components/forms/fields/RegionsField';
import SubmitButton from '@/components/forms/buttons/SubmitButton';

export default {
    name: 'Dashboard',
    data() {
        return {
            isFormProcessing: false,
        };
    },
    watch: {
        parentSource() {
            this.source = null;
        },
    },
    computed: {
        isAllFormFieldsValid() {
            return !this.$v.$invalid;
        },

        region: {
            get() {
                return this.$store.getters['canvasser/getRegion'];
            },
            set(value) {
                this.$store.commit('canvasser/updateRegion', value);
            },
        },
        source: {
            get() {
                return this.$store.getters['canvasser/getSource'];
            },
            set(value) {
                this.$store.commit('canvasser/updateSource', value);
            },
        },
        parentSource: {
            get() {
                return this.$store.getters['canvasser/getParentSource'];
            },
            set(value) {
                this.$store.commit('canvasser/updateParentSource', value);
            },
        },
        showSubSourcesField() {
            return this.parentSource && this.parentSource !== this.source;
        },
    },
    mounted() {
        this.$store.dispatch('account/fetchUserData', true);
        this.resetRegion();
        this.resetSource();
        this.resetRegistrationProgress();
    },
    methods: {
        /**
         * Force required validation message to be visible if user tries to submit an empty form.
         *
         * @returns {void}
         */
        forceRequiredValidationMessagesVisibility() {
            this.region = this.region || {};
            this.source = this.source || {};
        },

        /**
         * Redirect the user to the next step.
         *
         * @returns {void}
         */
        nextStep() {
            this.$router.push({ name: 'canvasser-register.account' });
        },

        /**
         * Reset the selected region.
         *
         * @returns {void}
         */
        resetRegion() {
            this.region = null;
        },

        /**
         * Reset the current registration progress.
         *
         * @returns {void}
         */
        resetRegistrationProgress() {
            this.$store.commit('register/resetState');
        },

        /**
         * Reset the selected source.
         *
         * @returns {void}
         */
        resetSource() {
            this.source = null;
        },

        /**
         * Handle form submission.
         *
         * @returns {void}
         */
        submitCurrentStep() {
            this.toggleFormProcessing();
            this.useParentSourceIfNecessary();

            // Provide delay to allow two way data binding of children components.
            setTimeout(() => {
                this.validateStep();
            }, 1000);
        },

        /**
         * Use parent source as a source if necessary (child source wasn't set).
         *
         * @returns {void}
         */
        useParentSourceIfNecessary() {
            if (this.parentSource && !this.source) {
                this.source = this.parentSource;
            }
        },

        /**
         * Validate current step before allowing user to continue with registration process.
         *
         * @returns {void}
         */
        validateStep() {
            this.$v.$touch();

            this.isAllFormFieldsValid ? this.nextStep() : this.forceRequiredValidationMessagesVisibility();

            this.toggleFormProcessing();
        },

        /**
         * Update form processing state.
         *
         * @returns {void}
         */
        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },
    },
    validations: {
        region: {
            required,
        },
        source: {
            required,
        },
    },
    components: {
        RegionsField,
        SourcesField,
        SubmitButton,
    },
};
</script>
